import { OFFER_TYPE } from '~/campaigns/consts/partners.consts'
import usePartnerCustomPrice, { PartnerIntervalPrice } from '~/campaigns/hooks/usePartnerCustomPrice'
import { DiscountDuration, PartnerDetailsType, PartnerInterval } from '~/campaigns/types/campaign.types'
import { partnerCouponFor } from '~/campaigns/utils/helper'

export interface PartnerIntervalOffer extends PartnerIntervalPrice {
  /** The partner prices this interval (own price, coupon or both); otherwise the geo price applies. */
  isPartner: boolean
  /** The partner's own Stripe price; unset checks out on the geo price. */
  priceId?: string
  coupon?: string
  /** Display-only, mirroring the coupon the checkout applies. */
  percentOff?: number
  duration: DiscountDuration
}

export interface PartnerOffer {
  monthly: PartnerIntervalOffer
  yearly: PartnerIntervalOffer
  currency?: string
  isLoading: boolean
}

const toIntervalOffer = (
  partnerDetails: PartnerDetailsType | undefined,
  interval: PartnerInterval,
  price: PartnerIntervalPrice,
): PartnerIntervalOffer => {
  const priceId = partnerDetails?.customPriceData?.[`${interval}PriceId`]
  const coupon = partnerCouponFor(partnerDetails, interval)
  const offerType = partnerDetails?.offerType

  return {
    ...price,
    isPartner: Boolean(priceId || coupon) && !price.isUnavailable,
    priceId,
    coupon,
    percentOff:
      coupon && offerType === OFFER_TYPE.PROCENTAGE_OFF
        ? partnerDetails?.procentageOff?.[interval] || undefined
        : undefined,
    duration: partnerDetails?.discountDuration?.[interval] ?? 'forever',
  }
}

/** The one rule for what a partner changes on a price page, per interval: price id, coupon and discount. */
export default function usePartnerOffer(
  partnerDetails: PartnerDetailsType | undefined,
  numberOfLicenses: number,
): PartnerOffer {
  const partnerPrice = usePartnerCustomPrice(partnerDetails, numberOfLicenses)

  return {
    monthly: toIntervalOffer(partnerDetails, 'monthly', partnerPrice.monthly),
    yearly: toIntervalOffer(partnerDetails, 'yearly', partnerPrice.yearly),
    currency: partnerPrice.currency,
    isLoading: partnerPrice.isLoading,
  }
}
